import { Component } from 'preact'
import { BaseFilterSelectorBuilderData, FilterSelectorProps } from './interfaces'
import SearchBox from '../../../preact-components/search-box/SearchBox'
import Fetch from '../../../preact-components/selector/Fetch'
import Icon from '../../../preact-components/assets'
import FilterOption, { FilterOptionItem } from './FilterOption'
import { extractRenderPropsChildren } from '../../../libs/renderProps'
import { FilterSelector } from '@insided/deprecated-control/overview/filters/FiltersMenu'

interface CommonProps {
  normalizer?: (items: any) => FilterOptionItem[]
  inputType?: string
  fetchUrl?: string
  onLoad?: (id: string, normalizedItems: FilterOptionItem[]) => void
  isSearchable?: boolean
  items?: FilterOptionItem[]
}

type BuilderData = BaseFilterSelectorBuilderData & CommonProps

type Filter = FilterSelector & CommonProps

type Props = FilterSelectorProps<FilterOptionItem[]> & CommonProps

interface State {
  data: FilterOptionItem[]
  fetchedItems: FilterOptionItem[]
  value: string
  isSelectAll?: boolean
}

class DynamicFilterSelector extends Component<Props, State> {
  static create = (config: BuilderData): Filter => {
    return {
      component: this,
      type: 'filter',
      getPillDisplayValue: (data: FilterOptionItem[]): string => {
        const firstItem = data[0].name
        const otherItems = data.length > 1 ? `+${data.length - 1}` : ''

        return `${firstItem} ${otherItems}`
      },
      ...config
    }
  }

  constructor(props: Props) {
    super(props)
    this.state = {
      data: this.props.selectedFilterItems || [],
      fetchedItems: [],
      value: '',
      isSelectAll: false
    }
  }

  onFilterOptionSelection = (payload: string[]) => {
    const { fetchedItems } = this.state
    const data = payload.map((item) => fetchedItems.filter((x) => String(x.id) === String(item))[0])
    this.setState({ data, isSelectAll: false })
  }

  onClickSelectAll = () => {
    this.setState({ isSelectAll: !this.state.isSelectAll, data: [] })
    if (this.state.isSelectAll) {
      this.setState({
        data: this.state.fetchedItems.filter((x) => String(x.id) !== undefined)
      })
    }
  }

  normalizeFetchedItems = (items: FilterOptionItem[]): FilterOptionItem[] =>
    items.map((item) => ({
      id: item.id,
      name: item.name,
      avatar: item.avatar,
      value: item.value || item.id,
      icon: Object.keys(item).includes('icon') ? item.icon : undefined
    }))

  responseKey = (filterId: string) => {
    return ['productArea'].includes(filterId) ? 'result' : undefined
  }

  renderItem = (item: FilterOptionItem) => {
    if (item.name === 'separator') {
      return <hr className='separator' />
    }

    if (item.name === 'title') {
      return (
        <div className='overview__filters-box-filter overview__filters-box-filter-title'>
          {item.value}
        </div>
      )
    }

    const { inputType } = this.props
    const { data, isSelectAll } = this.state

    return (
      <FilterOption
        item={item}
        key={item.id}
        type={inputType}
        data={data.map((y) => String(y.value))}
        onFilterOptionSelection={this.onFilterOptionSelection}
        isSelectAll={isSelectAll}
      />
    )
  }

  renderContent = (loading: boolean) => {
    if (loading) {
      return (
        <div className='loader'>
          <Icon icon='loader' />
        </div>
      )
    }

    const { fetchedItems, isSelectAll } = this.state
    const normalizedItems = this.normalizeFetchedItems(fetchedItems)
    if (normalizedItems && normalizedItems.length === 0) {
      return <div className='overview__filters-box-content-no-results'>No results found</div>
    }

    const { inputType } = this.props
    const selectionLabel = isSelectAll ? 'Deselect all' : 'Select all'

    return (
      <div>
        {inputType && inputType !== 'radio' && (
          <p className='select-all' onClick={this.onClickSelectAll}>
            {selectionLabel}
          </p>
        )}
        {normalizedItems.map(this.renderItem)}
      </div>
    )
  }

  onLoaded = (fetchedItems: any[]) => {
    const { onLoad, normalizer, items, id } = this.props
    const { data, value } = this.state

    if (!onLoad) {
      return
    }

    if (normalizer) {
      fetchedItems = normalizer(fetchedItems)
    }
    if (!value && items) fetchedItems = [...items, ...fetchedItems]
    const normalizedItems = this.normalizeFetchedItems(fetchedItems)
    this.setState(() => {
      const allItems = [...data, ...normalizedItems].filter(
        (item, index, self) => index === self.findIndex((x) => x.id === item.id)
      )
      if (allItems.length === this.state.data.length) {
        return { fetchedItems: allItems, isSelectAll: true }
      }

      return { fetchedItems: allItems }
    })
    onLoad(id, normalizedItems)
  }

  render() {
    const { id, name, children, fetchUrl, isSearchable = true } = this.props
    const { data, value } = this.state

    return (
      <div>
        <div className='overview__filters-box-content'>
          {isSearchable && (
            <SearchBox
              isModerationOverview
              placeholder={`Search for ${name}`}
              value={value}
              onSubmit={() => {}}
              onInput={(e) => this.setState({ value: (e.target as HTMLInputElement).value })}
              onClear={() => this.setState({ value: '' })}
            />
          )}
          <Fetch
            baseUrl={fetchUrl || ''}
            responseKey={this.responseKey(id)}
            searchValue={value}
            onLoaded={this.onLoaded}
          >
            {({ loading }) => this.renderContent(loading)}
          </Fetch>
        </div>
        {extractRenderPropsChildren(children)({
          data,
          isApplyButtonDisabled: data.length === 0
        })}
      </div>
    )
  }
}

export default DynamicFilterSelector
